/**
 * Task Y: bring-your-own custom domains — shared rules for the API (normalising, provider detection from NS records)
 * and the owner app (picture guides per provider).
 *
 * Model: the shop buys a domain anywhere and points www.<domain> at our CNAME target (sites.<platform domain>). The
 * bare domain <domain> either forwards to www at the registrar (URL forwarding — nearly every registrar has it) or,
 * where the DNS host supports it (Cloudflare CNAME flattening, ALIAS / ANAME records), points at the same target and
 * we redirect it to www ourselves.
 */

/** two-label public suffixes we meet in practice (MY / SEA / common); everything else is treated as one label */
const MULTI_SUFFIXES = new Set([
  "com.my", "net.my", "org.my", "edu.my", "gov.my", "name.my", "biz.my", "mil.my",
  "com.sg", "net.sg", "org.sg", "edu.sg", "per.sg",
  "co.id", "web.id", "or.id", "my.id", "biz.id",
  "com.au", "net.au", "org.au", "id.au", "co.nz", "co.uk", "org.uk", "me.uk", "ltd.uk",
  "com.hk", "org.hk", "net.hk", "com.tw", "org.tw", "idv.tw", "com.cn", "net.cn", "org.cn",
  "co.th", "in.th", "com.ph", "net.ph", "com.vn", "co.jp", "ne.jp", "co.kr", "co.in", "net.in", "com.bn",
]);

const HOST_RE = /^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z][a-z0-9-]{1,62}$/;

/** "https://WWW.AuraSalon.com/book?x" -> "www.aurasalon.com" (null when it can't be a hostname) */
export function normalizeDomainInput(input: string): string | null {
  let h = String(input ?? "").trim().toLowerCase();
  h = h.replace(/^[a-z][a-z0-9+.-]*:\/\//, "").replace(/[/?#].*$/, "").replace(/:\d+$/, "").replace(/\.$/, "").replace(/^\*\./, "");
  if (!h || h.length > 253 || !HOST_RE.test(h)) return null;
  return h;
}

/** the registrable domain (what you buy): www.aurasalon.com.my -> aurasalon.com.my */
export function registrableDomain(host: string): string {
  const parts = host.split(".");
  if (parts.length <= 2) return host;
  const last2 = parts.slice(-2).join(".");
  return MULTI_SUFFIXES.has(last2) ? parts.slice(-3).join(".") : last2;
}

export interface DomainPlan {
  /** the address the site is served on (www.<domain> when the shop typed the bare domain) */
  primary: string;
  /** the bought domain */
  apex: string;
  /** true when primary = www.<apex>, so the bare domain should forward / alias to it */
  withApex: boolean;
}
/** what we connect for what the owner typed: "aurasalon.com" -> www.aurasalon.com (+ bare domain forwarding) */
export function planDomain(input: string): DomainPlan | null {
  const h = normalizeDomainInput(input);
  if (!h) return null;
  const apex = registrableDomain(h);
  if (h === apex) return { primary: "www." + apex, apex, withApex: true };
  if (h === "www." + apex) return { primary: h, apex, withApex: true };
  return { primary: h, apex, withApex: false };
}
/** DNS "name" of the primary inside its zone (www, booking, …) */
export function hostLabel(primary: string, apex: string): string {
  return primary === apex ? "@" : primary.slice(0, -(apex.length + 1));
}

export type DnsProviderId = "cloudflare" | "namecheap" | "godaddy" | "exabytes" | "shinjiru" | "ipserverone" | "porkbun" | "squarespace" | "hostinger" | "generic";

export interface DnsProviderInfo {
  id: DnsProviderId;
  name: string;
  /** name-server host patterns (lower-case, tested against each NS) */
  ns: RegExp[];
  /** the bare domain can point at our CNAME target (CNAME flattening / ALIAS / ANAME) */
  apexAlias: boolean;
  /** the registrar / DNS host offers URL forwarding (bare domain -> https://www.…) */
  forwarding: boolean;
  /** where the owner logs in (shown in the guide) */
  loginUrl: string;
}

export const DNS_PROVIDERS: DnsProviderInfo[] = [
  { id: "cloudflare", name: "Cloudflare", ns: [/\.ns\.cloudflare\.com$/, /cloudflare/], apexAlias: true, forwarding: false, loginUrl: "dash.cloudflare.com" },
  { id: "namecheap", name: "Namecheap", ns: [/registrar-servers\.com$/, /namecheaphosting\.com$/, /namecheap/], apexAlias: true, forwarding: true, loginUrl: "namecheap.com" },
  { id: "godaddy", name: "GoDaddy", ns: [/domaincontrol\.com$/, /godaddy/], apexAlias: false, forwarding: true, loginUrl: "godaddy.com" },
  { id: "exabytes", name: "Exabytes", ns: [/exabytes/, /exa\.net\.my$/], apexAlias: false, forwarding: true, loginUrl: "exabytes.my" },
  { id: "shinjiru", name: "Shinjiru", ns: [/shinjiru/], apexAlias: false, forwarding: true, loginUrl: "shinjiru.com" },
  { id: "ipserverone", name: "IPSERVERONE / MYNIC", ns: [/ipserverone/, /ip1\.my$/, /mynic/, /\.my\.cloud$/], apexAlias: false, forwarding: true, loginUrl: "ipserverone.com" },
  { id: "porkbun", name: "Porkbun", ns: [/porkbun\.com$/], apexAlias: true, forwarding: true, loginUrl: "porkbun.com" },
  { id: "squarespace", name: "Squarespace (Google Domains)", ns: [/googledomains\.com$/, /squarespace/, /^ns-cloud-[a-z0-9]+\.google/], apexAlias: false, forwarding: true, loginUrl: "account.squarespace.com/domains" },
  { id: "hostinger", name: "Hostinger", ns: [/dns-parking\.com$/, /hostinger/], apexAlias: false, forwarding: true, loginUrl: "hpanel.hostinger.com" },
  { id: "generic", name: "Other provider", ns: [], apexAlias: false, forwarding: true, loginUrl: "" },
];
export const DNS_PROVIDER_IDS = DNS_PROVIDERS.map((p) => p.id);
export const dnsProvider = (id: string | null | undefined): DnsProviderInfo => DNS_PROVIDERS.find((p) => p.id === id) ?? DNS_PROVIDERS[DNS_PROVIDERS.length - 1]!;

/** which DNS host serves the domain, from its NS records ("generic" when unknown / none) */
export function detectDnsProvider(nameservers: readonly string[]): DnsProviderId {
  const ns = nameservers.map((n) => n.toLowerCase().replace(/\.$/, ""));
  for (const p of DNS_PROVIDERS) if (p.ns.length && ns.some((n) => p.ns.some((re) => re.test(n)))) return p.id;
  return "generic";
}

/**
 * Why a domain isn't live yet / any more — the owner app explains each in plain words (en / 中文):
 * record_missing (no CNAME yet), wrong_value (CNAME / A points elsewhere), proxied (Cloudflare orange cloud),
 * propagating (record is right, waiting for the world to see it / Cloudflare to confirm), ssl_pending (certificate
 * being issued), ownership_pending (Cloudflare asks for the TXT record), apex_missing (bare domain not forwarded —
 * informational), cf_unconfigured (automatic SSL not set up on the platform yet), cf_error (Cloudflare refused it),
 * domain_expired (no name servers any more), timeout (gave up after 15 days).
 */
export const DOMAIN_PROBLEMS = ["record_missing", "wrong_value", "proxied", "propagating", "ssl_pending", "ownership_pending", "apex_missing", "cf_unconfigured", "cf_error", "domain_expired", "timeout"] as const;
export type DomainProblem = (typeof DOMAIN_PROBLEMS)[number];

/** Cloudflare edge ranges (IPv4) — an A answer inside them for a host without our CNAME = proxied (orange cloud) */
const CF_V4 = ["173.245.48.0/20", "103.21.244.0/22", "103.22.200.0/22", "103.31.4.0/22", "141.101.64.0/18", "108.162.192.0/18", "190.93.240.0/20", "188.114.96.0/20", "197.234.240.0/22", "198.41.128.0/17", "162.158.0.0/15", "104.16.0.0/13", "104.24.0.0/14", "172.64.0.0/13", "131.0.72.0/22"];
const ip4 = (s: string) => s.split(".").reduce((n, o) => n * 256 + (Number(o) & 255), 0);
export function isCloudflareIp(ip: string): boolean {
  if (!/^\d+\.\d+\.\d+\.\d+$/.test(ip)) return false;
  const v = ip4(ip);
  return CF_V4.some((c) => { const [b, m] = c.split("/"); const size = 2 ** (32 - Number(m)); const start = ip4(b!); return v >= start && v < start + size; });
}
